import { deleteToken, getToken, Messaging } from 'firebase/messaging';
import { notificationsApi } from '@data/api/notifications';
import { isOlderThanOneDay } from '@data/utility/dates';
import {
  getPath,
  ROUTE_COMPANY_CLIENTS_SECTION,
  ROUTE_RESERVATIONS,
  ROUTE_RESERVATION_SLOT,
  ROUTE_RESERVATION_TIME,
} from '@data/router/consts';
import { logOnError } from '@src/@data/utility/errors';

export const saveAskedPermission = () => {
  try {
    localStorage.setItem(
      'portal_asked_notification_permission',
      new Date().toISOString()
    );
  } catch (error) {}
};

export const hasAskedPermission = () => {
  try {
    const timestamp = localStorage.getItem(
      'portal_asked_notification_permission'
    );
    return !!timestamp && !isOlderThanOneDay(new Date(timestamp), new Date());
  } catch (error) {
    return false;
  }
};

export const hasNotificationPermission = () => {
  try {
    return Notification.permission === 'granted';
  } catch (error) {
    return false;
  }
};

export const requestNotificationPermission = async () => {
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    logOnError(error);
    return false;
  }
};

export const registerToken = async (
  messaging: Messaging
): Promise<{ token?: string; error?: unknown }> => {
  if ('serviceWorker' in navigator) {
    try {
      // Sometimes service worker is not ready on token registration
      const currentToken = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      });

      await notificationsApi.registerToken(currentToken, '1.0.0');
      return { token: currentToken };
    } catch (error) {
      return { error };
    }
  } else {
    return Promise.resolve({ error: 'Service workers are not supported' });
  }
};

export const unregisterToken = async (messaging: Messaging, token: string) => {
  try {
    await notificationsApi.unregisterToken(token);
    await deleteToken(messaging);
  } catch (error) {
    logOnError(error);
  }
};

/**
 * Keep in sync with same fn in both firebase service workers
 */
export const parseDeepLinkToPortalLink = (deepLink?: string) => {
  if (!deepLink) {
    return;
  }

  const paths = deepLink.split('/');

  const businessIndex = paths.findIndex((param) => param === 'business');
  const companyId = businessIndex !== -1 ? paths[businessIndex + 1] : undefined;

  if (!companyId) {
    return;
  }

  const reservationsIndex = paths.findIndex(
    (param) => param === 'reservations'
  );
  const reservationId =
    reservationsIndex !== -1 ? paths[reservationsIndex + 1] : undefined;

  if (reservationsIndex !== -1 && !reservationId) {
    return getPath(ROUTE_RESERVATIONS, { companyId });
  }
  if (reservationsIndex !== -1 && reservationId) {
    return getPath(ROUTE_RESERVATION_TIME, { companyId, reservationId });
  }

  const clientsIndex = paths.findIndex((param) => param === 'clients');
  const clientId = clientsIndex !== -1 ? paths[clientsIndex + 1] : undefined;

  if (clientId) {
    return getPath(ROUTE_COMPANY_CLIENTS_SECTION, { companyId, clientId });
  }

  const slotsIndex = paths.findIndex((param) => param === 'slots');
  const slotId = slotsIndex !== -1 ? paths[slotsIndex + 1] : undefined;

  if (slotId) {
    return getPath(ROUTE_RESERVATION_SLOT, { companyId, slotId });
  }
};
